import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatBlock = ({ label, value, testId }) => (
  <div className="text-center border rounded-lg p-3" data-testid={testId}>
    <p className="text-xl font-bold text-gray-900">{value ?? '-'}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const GrupoStats = ({ title, stats, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent className="grid grid-cols-2 gap-2">
      <StatBlock label="N" value={stats?.n} />
      <StatBlock label="Finalizados" value={stats?.finalizados} />
      <StatBlock label="Tasa finalización" value={stats?.n ? `${stats.tasa_finalizacion_pct}%` : '-'} />
      <StatBlock label="Mejoraron de color" value={stats?.mejoraron_de_color_pct != null ? `${stats.mejoraron_de_color_pct}%` : 'Sin semana anterior'} />
    </CardContent>
  </Card>
);

const PersistenciaMedicion = ({ token, instances }) => {
  const [eficiencia, setEficiencia] = useState(null);
  const [impacto, setImpacto] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapActual, setSnapActual] = useState('');
  const [snapAnterior, setSnapAnterior] = useState('');
  const [instanceFilter, setInstanceFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const loadEficiencia = useCallback(async () => {
    try {
      const params = instanceFilter ? `?instance=${instanceFilter}` : '';
      const response = await fetch(`${API_URL}/api/persistencia/medicion/eficiencia${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error cargando eficiencia');
      setEficiencia(await response.json());
    } catch (error) {
      toast.error(error.message);
    }
  }, [token, instanceFilter]);

  const loadSnapshots = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/persistencia/snapshots`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await response.json();
    setSnapshots(d.snapshots || []);
    if (d.snapshots?.length > 0) setSnapActual(d.snapshots[0].snapshot_id);
  }, [token]);

  const loadImpacto = useCallback(async () => {
    if (!snapActual) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ snapshot_id_actual: snapActual });
      if (snapAnterior) params.append('snapshot_id_anterior', snapAnterior);
      if (instanceFilter) params.append('instance', instanceFilter);
      const response = await fetch(`${API_URL}/api/persistencia/medicion/impacto?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await response.json().catch(() => null);
      if (!response.ok) throw new Error(d?.detail || 'Error cargando el impacto');
      setImpacto(d);
    } catch (error) {
      toast.error(error.message);
      setImpacto(null);
    } finally {
      setLoading(false);
    }
  }, [token, snapActual, snapAnterior, instanceFilter]);

  useEffect(() => { loadEficiencia(); loadSnapshots(); }, [loadEficiencia, loadSnapshots]);
  useEffect(() => { loadImpacto(); }, [loadImpacto]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-gray-900">Medición</h3>
        <p className="text-sm text-gray-500">Etapa 1: con datos locales (last_access, avance de hito, finalización). Estadísticas de HubSpot (apertura/click) se conectan en Etapa 2.</p>
      </div>

      <select className="border rounded-md px-3 py-2 text-sm" value={instanceFilter} onChange={(e) => setInstanceFilter(e.target.value)} data-testid="medicion-instance-filter">
        <option value="">Todas las instancias</option>
        {(instances || []).map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
      </select>

      <Card data-testid="medicion-eficiencia-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">(a) Eficiencia de Campaña</CardTitle>
          <CardDescription>Cobertura de la matriz de Config de Acciones</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatBlock label="Filas totales" value={eficiencia?.total_filas_matriz} />
          <StatBlock label="Aprobadas" value={eficiencia?.aprobadas} />
          <StatBlock label="Pendientes" value={eficiencia?.pendientes_aprobacion} />
          <StatBlock label="Cobertura aprobación" value={eficiencia ? `${eficiencia.cobertura_aprobacion_pct}%` : '-'} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">(b) Acción vs Control</CardTitle>
          <CardDescription>Reactivación, avance de hito y finalización, comparando ambos grupos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <select className="border rounded-md px-3 py-2 text-sm" value={snapActual} onChange={(e) => setSnapActual(e.target.value)} data-testid="medicion-snapshot-actual-select">
              <option value="">Snapshot actual...</option>
              {snapshots.map((s) => <option key={s.snapshot_id} value={s.snapshot_id}>{s.snapshot_date} {s.created_at ? new Date(s.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''} ({s.total_students} alumnos)</option>)}
            </select>
            <select className="border rounded-md px-3 py-2 text-sm" value={snapAnterior} onChange={(e) => setSnapAnterior(e.target.value)} data-testid="medicion-snapshot-anterior-select">
              <option value="">(sin comparación histórica)</option>
              {snapshots.filter((s) => s.snapshot_id !== snapActual).map((s) => <option key={s.snapshot_id} value={s.snapshot_id}>{s.snapshot_date} {s.created_at ? new Date(s.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''} ({s.total_students} alumnos)</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={loadImpacto} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {!impacto ? (
            <p className="text-sm text-gray-400" data-testid="medicion-impacto-empty">Elegí un snapshot para ver la comparación.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <GrupoStats title="Grupo Acción" stats={impacto.grupo_accion} testId="medicion-grupo-accion" />
              <GrupoStats title="Grupo Control" stats={impacto.grupo_control} testId="medicion-grupo-control" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PersistenciaMedicion;
